import { useEffect, useCallback } from 'react';

// Type definitions for Performance API
interface PerformanceEventTiming extends PerformanceEntry {
  processingStart?: number;
  startTime: number;
}

interface PerformanceNavigationTiming extends PerformanceEntry {
  domainLookupStart: number;
  domainLookupEnd: number;
  connectStart: number;
  connectEnd: number;
  requestStart: number;
  responseStart: number;
  domContentLoadedEventEnd: number;
  loadEventEnd: number;
  fetchStart: number;
  transferSize?: number;
}

export const usePerformanceMonitoring = () => {
  // Measure Core Web Vitals
  const measureCoreWebVitals = useCallback(() => {
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          const lastEntry = entries[entries.length - 1];
          if (lastEntry && typeof lastEntry.startTime === 'number') {
            console.log('LCP:', lastEntry.startTime);
            
            // Send to analytics if LCP is poor (>2.5s)
            if (lastEntry.startTime > 2500) {
              console.warn('Poor LCP detected:', lastEntry.startTime);
            }
          }
        }
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.log('LCP observer error:', e);
      }

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fidEntry = entry as PerformanceEventTiming;
          if (fidEntry.processingStart && fidEntry.startTime) {
            const fid = fidEntry.processingStart - fidEntry.startTime;
            console.log('FID:', fid);
            
            // Send to analytics if FID is poor (>100ms)
            if (fid > 100) {
              console.warn('Poor FID detected:', fid);
            }
          }
        });
      });
      
      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.log('FID observer error:', e);
      }

      // Cumulative Layout Shift (CLS)
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        const entries = list.getEntries();
        
        entries.forEach((entry: any) => {
          if (entry && typeof entry.value === 'number' && !entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        
        console.log('CLS:', clsValue);
        
        // Send to analytics if CLS is poor (>0.1)
        if (clsValue > 0.1) {
          console.warn('Poor CLS detected:', clsValue);
        }
      });
      
      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.log('CLS observer error:', e);
      }
    }
  }, []);

  // Measure page load performance
  const measurePageLoad = useCallback(() => {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigationEntries = performance.getEntriesByType('navigation');
        if (navigationEntries.length > 0) {
          const navigation = navigationEntries[0] as PerformanceNavigationTiming;
          try {
            const metrics = {
              // DNS lookup time
              dns: navigation.domainLookupEnd - navigation.domainLookupStart,
              // TCP connection time
              tcp: navigation.connectEnd - navigation.connectStart,
              // First byte time
              ttfb: navigation.responseStart - navigation.requestStart,
              // DOM content loaded
              domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
              // Page load complete
              loadComplete: navigation.loadEventEnd - navigation.fetchStart,
              // Total page size
              totalSize: navigation.transferSize || 0
            };

            console.log('Page Load Metrics:', metrics);
            
            // Send to analytics
            if (metrics.ttfb > 600) {
              console.warn('Slow TTFB detected:', metrics.ttfb);
            }
            
            if (metrics.loadComplete > 3000) {
              console.warn('Slow page load detected:', metrics.loadComplete);
            }
          } catch (error) {
            console.log('Error calculating navigation metrics:', error);
          }
        }
      }, 0);
    });
  }, []);

  // Measure resource loading performance
  const measureResourceLoading = useCallback(() => {
    if ('PerformanceObserver' in window) {
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (entry && typeof entry.duration === 'number') {
            const resourceMetrics = {
              name: entry.name || 'unknown',
              type: entry.initiatorType || 'unknown',
              duration: entry.duration,
              size: entry.transferSize || 0,
              startTime: entry.startTime || 0
            };

            // Log slow resources (>1s)
            if (entry.duration > 1000) {
              console.warn('Slow resource detected:', resourceMetrics);
            }
          }
        });
      });

      try {
        resourceObserver.observe({ entryTypes: ['resource'] });
      } catch (e) {
        console.log('Resource observer error:', e);
      }
    }
  }, []);

  // Measure memory usage
  const measureMemoryUsage = useCallback(() => {
    if ('memory' in performance && (performance as any).memory) {
      try {
        const memory = (performance as any).memory;
        if (memory && typeof memory.usedJSHeapSize === 'number' && typeof memory.jsHeapSizeLimit === 'number') {
          const memoryMetrics = {
            usedJSHeapSize: memory.usedJSHeapSize,
            totalJSHeapSize: memory.totalJSHeapSize || 0,
            jsHeapSizeLimit: memory.jsHeapSizeLimit
          };

          console.log('Memory Usage:', memoryMetrics);
          
          // Warn if memory usage is high (>80% of limit)
          const usagePercentage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
          if (usagePercentage > 80) {
            console.warn('High memory usage detected:', usagePercentage.toFixed(2) + '%');
          }
        }
      } catch (error) {
        console.log('Error measuring memory usage:', error);
      }
    }
  }, []);

  // Measure long tasks
  const measureLongTasks = useCallback(() => {
    if ('PerformanceObserver' in window) {
      const longTaskObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (entry && typeof entry.duration === 'number') {
            console.warn('Long task detected:', {
              duration: entry.duration,
              startTime: entry.startTime || 0,
              name: entry.name || 'unknown'
            });
          }
        });
      });

      try {
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        console.log('Long task observer error:', e);
      }
    }
  }, []);

  // Generate performance report
  const generatePerformanceReport = useCallback(() => {
    const report = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connection: (() => {
        try {
          if ((navigator as any).connection) {
            const connection = (navigator as any).connection;
            return {
              effectiveType: connection.effectiveType || 'unknown',
              downlink: connection.downlink || 0,
              rtt: connection.rtt || 0
            };
          }
        } catch (error) {
          console.log('Error getting connection metrics:', error);
        }
        return null;
      })(),
      memory: (() => {
        try {
          if ('memory' in performance && (performance as any).memory) {
            const memory = (performance as any).memory;
            if (memory && typeof memory.usedJSHeapSize === 'number' && typeof memory.jsHeapSizeLimit === 'number') {
              return {
                usedJSHeapSize: memory.usedJSHeapSize,
                totalJSHeapSize: memory.totalJSHeapSize || 0,
                jsHeapSizeLimit: memory.jsHeapSizeLimit
              };
            }
          }
        } catch (error) {
          console.log('Error getting memory metrics:', error);
        }
        return null;
      })(),
      navigation: (() => {
        try {
          const navigationEntries = performance.getEntriesByType('navigation');
          if (navigationEntries.length > 0) {
            const navigation = navigationEntries[0] as PerformanceNavigationTiming;
            return {
              dns: navigation.domainLookupEnd - navigation.domainLookupStart,
              tcp: navigation.connectEnd - navigation.connectStart,
              ttfb: navigation.responseStart - navigation.requestStart,
              domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
              loadComplete: navigation.loadEventEnd - navigation.fetchStart
            };
          }
        } catch (error) {
          console.log('Error getting navigation metrics:', error);
        }
        return null;
      })()
    };

    console.log('Performance Report:', report);
    return report;
  }, []);

  useEffect(() => {
    // Initialize all performance monitoring
    measureCoreWebVitals();
    measurePageLoad();
    measureResourceLoading();
    measureMemoryUsage();
    measureLongTasks();

    // Generate report after page load
    window.addEventListener('load', () => {
      setTimeout(generatePerformanceReport, 1000);
    });

    // Monitor memory usage periodically
    const memoryInterval = setInterval(measureMemoryUsage, 30000); // Every 30 seconds

    return () => {
      clearInterval(memoryInterval);
    };
  }, [
    measureCoreWebVitals,
    measurePageLoad,
    measureResourceLoading,
    measureMemoryUsage,
    measureLongTasks,
    generatePerformanceReport
  ]);

  return {
    generatePerformanceReport,
    measureMemoryUsage
  };
};
